import {
    Address,
    Classification,
    DocumentStatus,
    FunctionalAsset,
    GenericObject,
    Media,
    ReceiptAlert,
    copyFields,
    fixDates,
} from '@synergy/models';

import { Account } from '../../../common/models/account';
import { Contact } from '../../../common/models/contact';
import { Customer } from '../../../common/models/customer';
import { InvoiceGenericItem } from './invoice-generic-item';
import { InvoiceProductItem } from './invoice-product-item';
import { InvoiceTextItem } from './invoice-text-item';
import { InvoiceType } from './invoice-type';
import { PaymentTerm } from '../../../common/models/payment-term';
import { PoolBankInfo } from './../../../common/models/pool-bank-info';
import { Supplier } from '../../../common/models/supplier';
import { User } from '../../../common/models/user';

export class Invoice {
    public id!: number;
    public number!: string;
    public orderReference!: string;
    public salesOrderNumber!: string;
    public salesOrderId!: number;
    public orderDate!: Date;
    public recipientOrderNumber!: string;
    public costCentre!: string;
    public functionalAsset!: FunctionalAsset;
    public functionalAssetId!: number;
    public poolBankInfo!: PoolBankInfo;
    public poolBankInfoId!: number;
    public requireFunctionalAssets!: boolean;
    public internalNote!: string;
    public noteToRecipient!: string;
    public type!: InvoiceType;
    public status!: DocumentStatus;
    public shouldSendEmail?: boolean;
    public netShippingCosts!: number;
    public netSurcharge!: number;
    public avisoContact!: Contact;
    public responsibleContact!: Contact;
    public recipientContact!: Contact;
    public recipientAccount!: Account;
    public recipientOrderContact?: Contact;
    public recipientPaymentTermId?: number;
    public supplierContact!: Contact;
    public supplierAccount!: Supplier;
    public supplierPaymentTermId?: number;
    public supplierTermsOfDelivery?: string;
    public supplierPoolPaymentTerm?: PaymentTerm;
    public currency!: string;
    public isTaxFree!: boolean;
    public isCredit!: boolean;
    public totalPrice!: number;
    public totalNetPrice!: number;
    public deliveryAddress!: Address;
    public invoiceAddress?: Address;
    public items!: InvoiceProductItem[];
    public textItems!: InvoiceTextItem[];
    public surchargeItems: InvoiceGenericItem[] = [];
    public media!: Media[];
    public receiptAlert?: ReceiptAlert;
    public createdAt!: Date;
    public updatedAt?: Date;
    public createdBy!: User;
    public updatedBy?: User;

    public invoiceDate!: Date;
    public deliveryDate!: Date;
    public deliveryNumber!: string;
    public paymentTerm!: PaymentTerm;
    public paymentTermId?: number;
    public incomingInvoiceNumber?: string;
    public classifications?: Classification[];
    /** When the invoice went to BMD; until then a sent invoice's lines may change their account (#2326 R-239). */
    public exportedToAccountingAt?: string | null;

    public static fromPlainObject(object: GenericObject): Invoice {
        const invoice = new Invoice();

        copyFields(object, invoice);

        if (object['avisoContact']) {
            invoice.avisoContact = Contact.fromPlainObject(object['avisoContact']);
        }

        if (object['responsibleContact']) {
            invoice.responsibleContact = Contact.fromPlainObject(object['responsibleContact']);
        }

        if (object['recipientContact']) {
            invoice.recipientContact = Contact.fromPlainObject(object['recipientContact']);
        }

        if (object['recipientOrderContact']) {
            invoice.recipientOrderContact = Contact.fromPlainObject(object['recipientOrderContact']);
        }

        if (object['recipientAccount']) {
            invoice.recipientAccount = Customer.fromPlainObject(object['recipientAccount']);
        }

        if (object['supplierContact']) {
            invoice.supplierContact = Contact.fromPlainObject(object['supplierContact']);
        }

        if (object['supplierAccount']) {
            invoice.supplierAccount = Supplier.fromPlainObject(object['supplierAccount']);
        }

        if (object['deliveryAddress']) {
            invoice.deliveryAddress = Address.fromPlainObject(object['deliveryAddress']);
        }

        if (object['invoiceAddress']) {
            invoice.invoiceAddress = Address.fromPlainObject(object['invoiceAddress']);
        }

        if (object['items']) {
            invoice.items = object['items'].map(InvoiceProductItem.fromPlainObject);
        }

        if (object['media']) {
            invoice.media = object['media'].map(Media.fromPlainObject);
        }

        if (object['poolBankInfo']) {
            invoice.poolBankInfo = { ...object['poolBankInfo'] };
        }

        if (object['classifications']) {
            invoice.classifications = [...object['classifications']];
        }

        fixDates(invoice, ['orderDate', 'invoiceDate', 'deliveryDate', 'createdAt', 'updatedAt']);

        return invoice;
    }
}
